/**
 * Sheet Metal Workbench Integration for CAD Viewer
 * This extends the CADViewer class with sheet metal operations
 */

// Extend CADViewer prototype with sheet metal methods
if (typeof CADViewer !== 'undefined') {

  // ===== Sheet Metal Operations =====

  /**
   * Create base flange (starting sheet metal part)
   */
  CADViewer.prototype.createSheetMetalBaseFlange = function() {
    if (!this.sheetMetal) {
      Utils.notify('Sheet metal workbench not initialized', 'error');
      return;
    }

    const width = 100;
    const length = 100;
    const thickness = parseFloat(document.getElementById('sheetMetalThickness')?.value || 1.0);

    const feature = this.sheetMetal.createBaseFlange({
      width,
      length,
      thickness,
      position: new THREE.Vector3(0, 0, 0)
    });

    if (feature) {
      Utils.notify('Base flange created', 'success');
      this.updateFeatureTree();
    }
  };

  /**
   * Create wall from selected edge
   */
  CADViewer.prototype.createSheetMetalWall = function() {
    if (!this.sheetMetal) {
      Utils.notify('Sheet metal workbench not initialized', 'error');
      return;
    }

    const angle = parseFloat(document.getElementById('sheetMetalBendAngle')?.value || 90);
    const length = parseFloat(document.getElementById('sheetMetalFlangeLength')?.value || 10);
    const bendRadius = parseFloat(document.getElementById('sheetMetalBendRadius')?.value || 1.5);
    const thickness = parseFloat(document.getElementById('sheetMetalThickness')?.value || 1.0);
    const kFactor = parseFloat(document.getElementById('sheetMetalKFactor')?.value || 0.44);

    if (this.sheetMetal.selectedEdges.length === 0) {
      Utils.notify('Select an edge first', 'warning');
      return;
    }

    const feature = this.sheetMetal.createWallFromEdge({
      angle,
      length,
      bendRadius,
      thickness,
      kFactor
    });

    if (feature) {
      Utils.notify(`Wall created (${angle}°)`, 'success');
      this.updateFeatureTree();
    }
  };

  /**
   * Create flange from selected edge
   */
  CADViewer.prototype.createSheetMetalFlange = function() {
    if (!this.sheetMetal) {
      Utils.notify('Sheet metal workbench not initialized', 'error');
      return;
    }

    const angle = parseFloat(document.getElementById('sheetMetalBendAngle')?.value || 90);
    const length = parseFloat(document.getElementById('sheetMetalFlangeLength')?.value || 10);
    const bendRadius = parseFloat(document.getElementById('sheetMetalBendRadius')?.value || 1.5);

    if (this.sheetMetal.selectedEdges.length === 0) {
      Utils.notify('Select an edge first', 'warning');
      return;
    }

    const feature = this.sheetMetal.createFlange({
      angle,
      length,
      bendRadius,
      relief: true
    });

    if (feature) {
      Utils.notify(`Flange created (${angle}°)`, 'success');
      this.updateFeatureTree();
    }
  };

  /**
   * Create hem on selected edge
   */
  CADViewer.prototype.createSheetMetalHem = function() {
    if (!this.sheetMetal) {
      Utils.notify('Sheet metal workbench not initialized', 'error');
      return;
    }

    if (this.sheetMetal.selectedEdges.length === 0) {
      Utils.notify('Select an edge first', 'warning');
      return;
    }

    const hemTypes = ['open', 'closed', 'teardrop', 'rolled'];
    const hemTypeChoice = prompt(`Hem type (${hemTypes.join(', ')}):`, 'open');
    const hemType = hemTypes.includes(hemTypeChoice) ? hemTypeChoice : 'open';

    const feature = this.sheetMetal.createHem({
      hemType,
      length: 5,
      gap: 0.5,
      radius: 1
    });

    if (feature) {
      Utils.notify(`${hemType} hem created`, 'success');
      this.updateFeatureTree();
    }
  };

  /**
   * Create junction between two walls
   */
  CADViewer.prototype.createSheetMetalJunction = function() {
    if (!this.sheetMetal) {
      Utils.notify('Sheet metal workbench not initialized', 'error');
      return;
    }

    Utils.notify('Select two walls to create junction', 'info');
    // TODO: Implement wall selection UI
  };

  /**
   * Create bend along selected line
   */
  CADViewer.prototype.createSheetMetalBend = function() {
    if (!this.sheetMetal) {
      Utils.notify('Sheet metal workbench not initialized', 'error');
      return;
    }

    const angle = parseFloat(document.getElementById('sheetMetalBendAngle')?.value || 90);
    const radius = parseFloat(document.getElementById('sheetMetalBendRadius')?.value || 1.5);
    const kFactor = parseFloat(document.getElementById('sheetMetalKFactor')?.value || 0.44);

    if (this.sheetMetal.selectedEdges.length === 0) {
      Utils.notify('Select a line for bend first', 'warning');
      return;
    }

    const feature = this.sheetMetal.createBend({
      angle,
      radius,
      kFactor,
      direction: 'up'
    });

    if (feature) {
      const ba = feature.params.bendAllowance.toFixed(2);
      const bd = feature.params.bendDeduction.toFixed(2);
      Utils.notify(`Bend created (BA: ${ba}mm, BD: ${bd}mm)`, 'success');
      this.updateFeatureTree();
    }
  };

  /**
   * Add corner relief to selected feature
   */
  CADViewer.prototype.addSheetMetalCornerRelief = function() {
    if (!this.sheetMetal) {
      Utils.notify('Sheet metal workbench not initialized', 'error');
      return;
    }

    const reliefType = document.getElementById('sheetMetalReliefType')?.value || 'rectangle';
    const activeFeature = this.sheetMetal.features.get(this.sheetMetal.activePartId);

    if (!activeFeature) {
      Utils.notify('Select a feature first', 'warning');
      return;
    }

    this.sheetMetal.addCornerRelief(activeFeature, reliefType);
    Utils.notify(`${reliefType} corner relief added`, 'success');
  };

  /**
   * Unfold sheet metal part to flat pattern
   */
  CADViewer.prototype.unfoldSheetMetalPart = function() {
    if (!this.sheetMetal) {
      Utils.notify('Sheet metal workbench not initialized', 'error');
      return;
    }

    const unfoldedState = this.sheetMetal.unfoldPart();
    if (unfoldedState) {
      const bounds = unfoldedState.flatPattern.boundingBox;
      Utils.notify(
        `Part unfolded (${bounds.width.toFixed(1)}mm x ${bounds.height.toFixed(1)}mm)`,
        'success'
      );
    }
  };

  /**
   * Fold sheet metal part back to 3D
   */
  CADViewer.prototype.foldSheetMetalPart = function() {
    if (!this.sheetMetal) {
      Utils.notify('Sheet metal workbench not initialized', 'error');
      return;
    }

    this.sheetMetal.foldPart();
    Utils.notify('Part folded back to 3D', 'success');
  };

  /**
   * Export flat pattern
   */
  CADViewer.prototype.exportSheetMetalFlatPattern = function() {
    if (!this.sheetMetal) {
      Utils.notify('Sheet metal workbench not initialized', 'error');
      return;
    }

    const dxfData = this.sheetMetal.exportFlatPattern();
    if (!dxfData) {
      Utils.notify('Unfold the part first to export flat pattern', 'warning');
      return;
    }

    const jsonString = JSON.stringify(dxfData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'flat_pattern.json';
    link.click();

    Utils.notify('Flat pattern exported', 'success');
  };

  /**
   * Update sheet metal parameter
   */
  CADViewer.prototype.updateSheetMetalParameter = function(paramName, value) {
    if (!this.sheetMetal) return;

    const params = {};
    params[paramName] = value;
    this.sheetMetal.updateParameters(params);

    console.log(`[CADViewer] Sheet metal parameter updated: ${paramName} = ${value}`);
  };

  console.log('[SheetMetalIntegration] CADViewer extended with sheet metal operations');
}
